import {
  type DecisionExportFormat,
  type DecisionSheetView,
  type DocumentDetail,
} from '@apm/contracts';
import { ApiRequestError } from '../../lib/http';
import { uploadFile } from '../../lib/upload';
import { documentApi } from '../documents/api';
import { decisionSheetApi } from './api';

export interface SheetExportResult {
  readonly document: DocumentDetail;
  /** The version this export wrote — 1 for a new document. */
  readonly version: number;
  /** `true` = a new project document; `false` = a new version of the previous export. */
  readonly created: boolean;
}

/** Fields of the view the export reads — the previous export of each format. */
export type ExportableSheet = Pick<
  DecisionSheetView,
  'id' | 'projectId' | 'title' | 'status' | 'exportDocumentId' | 'exportMd'
>;

/**
 * The project document the previous export **of this format** went to.
 * `.chilldoc` keeps the original columns (`exportDocumentId`), `.md` its own
 * (`exportMd`) — one document per format, so a document's version history
 * never mixes the two (APM-DSX).
 */
export function previousExportId(
  view: Pick<DecisionSheetView, 'exportDocumentId' | 'exportMd'>,
  format: DecisionExportFormat,
): string | null {
  return format === 'md' ? (view.exportMd?.documentId ?? null) : view.exportDocumentId;
}

/**
 * The sheet is not closed — thrown **before** any upload, so the caller shows
 * the same "close the sheet first" dialog as for the server's
 * `DECISION_SHEET_NOT_CLOSED`.
 */
export class SheetNotClosedError extends Error {
  constructor(readonly status: 'draft' | 'open') {
    super(`Decision sheet is ${status}, not closed`);
    this.name = 'SheetNotClosedError';
  }
}

/**
 * The previous export, if it is still there. A soft-deleted document answers
 * 404 (`DocumentRepository.findById` filters `deletedAt`), and plan §6.1 says
 * an export must never add a version to a deleted document ⇒ `null`, and the
 * caller creates a new one. Any other error is real and propagates.
 */
async function previousExport(documentId: string | null): Promise<DocumentDetail | null> {
  if (!documentId) return null;
  try {
    return await documentApi.detail(documentId);
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) return null;
    throw error;
  }
}

/**
 * "Xuất thành tài liệu dự án" — APM-DSHEET-e + APM-DSX, plan §6.1. Uses the
 * same document endpoints as the rest of the web (`projectDocumentStorage`):
 *
 * - first export of a format: file into the project area (presign → PUT
 *   straight to S3 → commit, rule #3), then `POST /projects/:id/documents`
 *   with `kind: 'other'` and the sheet title;
 * - later exports: file into that format's document, then
 *   `POST /documents/:id/versions` — always a **new version**, never an
 *   overwrite (`/save`): each export is a dated snapshot of the sheet.
 *
 * Then `POST /decision-sheets/:id/exported {documentId, version, format}`.
 *
 * ## Only a closed sheet
 *
 * The server accepts the last step only when the sheet is `closed`
 * (`DECISION_SHEET_NOT_CLOSED`): an open sheet still changes, so the document
 * would be stale the moment it is written. The page checks `status` before
 * even opening the format dialog, and this function checks it again before
 * touching storage — a click on an open sheet uploads nothing, so no orphan
 * document. If someone reopens the sheet in between, the server refuses the
 * last step and the document / version just created is a spare one the sheet
 * does not point to — the same outcome as the failure below.
 *
 * ⚠️ Two transactions in two modules (the repo has no command-calls-command
 * pattern). If the last step fails, the document exists but the sheet does not
 * know it: the next export creates another document instead of a new version.
 * Accepted in plan §6.1 — the caller reports the error, nothing is lost.
 */
export async function exportSheetToDocument(
  view: ExportableSheet,
  format: DecisionExportFormat,
  file: File,
  changeNote?: string,
): Promise<SheetExportResult> {
  if (view.status !== 'closed') throw new SheetNotClosedError(view.status);
  const previous = await previousExport(previousExportId(view, format));

  let document: DocumentDetail;
  if (previous) {
    const fileId = await uploadFile(file, { projectId: view.projectId, documentId: previous.id });
    document = await documentApi.addVersion(previous.id, {
      fileId,
      ...(changeNote ? { changeNote } : {}),
    });
  } else {
    const fileId = await uploadFile(file, { projectId: view.projectId, purpose: 'document' });
    document = await documentApi.create(view.projectId, {
      title: view.title,
      kind: 'other',
      fileId,
    });
  }

  const version = document.latestVersionNo;
  await decisionSheetApi.recordExport(view.id, { documentId: document.id, version, format });
  return { document, version, created: previous === null };
}
